/**
 * useCreatorProfile — checks whether the authenticated user
 * already has a creator profile and determines the correct
 * action button / redirect target based on profile status.
 *
 * One authenticated user = one creator profile. This hook
 * enforces that on the frontend before any "Apply" button
 * is shown.
 */

import { useCallback, useEffect, useState } from 'react';
import { getSupabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/auth';
import type { ProfileStatus } from '@/types/database';

export type CreatorProfileAction =
  | { kind: 'apply'; label: string; path: string }
  | { kind: 'continue'; label: string; path: string }
  | { kind: 'view'; label: string; path: string }
  | { kind: 'review'; label: string; path: string }
  | { kind: 'dashboard'; label: string; path: string }
  | { kind: 'resubmit'; label: string; path: string };

export interface UseCreatorProfileResult {
  loading: boolean;
  hasProfile: boolean;
  status: ProfileStatus | null;
  action: CreatorProfileAction | null;
  refresh: () => void;
}

export function useCreatorProfile(): UseCreatorProfileResult {
  const { user, passwordRecovery } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<ProfileStatus | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!user || passwordRecovery) {
      setStatus(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const supabase = getSupabase();
        const { data, error } = await supabase
          .from('creator_profiles')
          .select('profile_status')
          .eq('user_id', user.id)
          .maybeSingle();

        if (cancelled) return;

        if (error) {
          setStatus(null);
          setLoading(false);
          return;
        }

        setStatus((data?.profile_status as ProfileStatus) ?? null);
        setLoading(false);
      } catch {
        if (cancelled) return;
        setStatus(null);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, passwordRecovery, refreshKey]);

  const hasProfile = status !== null;

  let action: CreatorProfileAction | null = null;

  if (!loading) {
    if (!user || passwordRecovery) {
      action = { kind: 'apply', label: 'Join the Creator Registry', path: '/apply' };
    } else if (!hasProfile) {
      action = { kind: 'apply', label: 'Create Your Creator Profile', path: '/application' };
    } else {
      switch (status) {
        case 'draft':
          action = { kind: 'continue', label: 'Continue Application', path: '/workspace/application' };
          break;
        case 'submitted':
          action = { kind: 'view', label: 'View Application', path: '/workspace/application' };
          break;
        case 'under_review':
          action = { kind: 'review', label: 'Under Review', path: '/workspace/status' };
          break;
        case 'approved':
          action = { kind: 'dashboard', label: 'Creator Dashboard', path: '/dashboard' };
          break;
        case 'rejected':
          action = { kind: 'resubmit', label: 'Edit & Resubmit', path: '/workspace/application' };
          break;
      }
    }
  }

  return { loading, hasProfile, status, action, refresh };
}
